import { useApolloClient } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import trim from '../trim';
import { GroupSelection, SelectableGroupFilter } from './GroupSelector.types';

export type GroupSelectionData = {
	allGroups: graphql.Group[];
	selectableGroups: graphql.Group[];
	groupsInSelection: graphql.Group[];
	groupSelection: GroupSelection[];
	hasExplicitGroupSelection: boolean;
	updateExplicitGroupSelection: (selection: GroupSelection[]) => void;
	loading: boolean;
};

/*
 * Hook which handles group selection state, to be used in concert with the GroupSelector component.
 *
 * A GroupSelection is a group and a 'recursive' flag indicating if all underlying groups should be selected as well
 *
 * This hooks maintains 'default' and 'explicit' selections.
 * - Default means: user did not select anything.
 * - Explicit means: user explicitly selected one or more groups
 * Explicit selections always override the default. When no explicit selection is made or is set to empty, the selection falls back to default.
 * We maintain these types of selections so that the GroupSelector component might choose to display the default selection or not.
 *
 * Note: this hook needs to retrieve group data before it can maintain groupselections. Make sure to wait for the `loading` state to be true before using its data.
 *
 * Returns object with the following values:
 * 	- allGroups: contains all groups within a workspace
 * 	- groupSelection the current GroupSelection
 * 	- selectableGroups: all groups that are selectable by the current user, defined by the `canSelect` option
 * 	- groupsInSelection: all groups that are currently covered by the groupSelection
 *  - hasExplicitGroupSelection: true when an explicit group selection has been applied
 *	- updateExplicitGroupSelection: function called when setting the current group selection, overriding the default
 *	- loading: true when group data is being loaded
 */
export function useGroupSelection(params: {
	localStorageKey?: string;
	setPermissionBasedDefault: boolean; // set to true to set default selection based on user's permissions. Set to false to leave initial selection empty.
	types?: graphql.GroupType[];
	canSelect: (group: graphql.Group) => boolean;
}): GroupSelectionData {
	const { localStorageKey, setPermissionBasedDefault, types, canSelect } = params;

	const [allGroups, setAllGroups] = useState<graphql.Group[]>([]);
	const [selectableGroups, setSelectableGroups] = useState<graphql.Group[]>([]);
	const [groupsInSelection, setGroupsInSelection] = useState<graphql.Group[]>([]);

	const [defaultGroupSelection, setDefaultGroupSelection] = useState<GroupSelection[]>([]);
	const [explicitGroupSelection, setExplicitGroupSelection] = useState<
		GroupSelection[] | undefined
	>(undefined);

	const [groupSelection, setGroupSelection] = useState<GroupSelection[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const explicitGroupSelectionRef = useRef(explicitGroupSelection);

	explicitGroupSelectionRef.current = explicitGroupSelection;

	const groups = useBatchedGroups({ types });

	useEffect(() => {
		if (!groups) {
			setLoading(true);
			return;
		}

		const newSelectableGroups = groups.filter((group) => {
			return canSelect(group);
		});

		const newDefaultGroupSelection: GroupSelection[] = [];
		if (setPermissionBasedDefault) {
			newDefaultGroupSelection.push(
				...newSelectableGroups.map((g) => {
					return { recursive: false, group: g };
				}),
			);
		}

		let newExplicitGroupSelection: GroupSelection[] | undefined = undefined;
		if (localStorageKey) {
			const groupsFromLocalStorage = groupsFromStorage(localStorageKey, newSelectableGroups) || [];
			if (groupsFromLocalStorage.length > 0) {
				newExplicitGroupSelection = groupsFromLocalStorage;
			}
		}

		if (newExplicitGroupSelection === undefined) {
			const currentExplicit = explicitGroupSelectionRef.current;
			if (currentExplicit && currentExplicit.length > 0) {
				const remapped = currentExplicit.flatMap(({ group, recursive }) => {
					const updated = newSelectableGroups.find((match) => match.id === group.id);
					return updated ? [{ group: updated, recursive }] : [];
				});
				if (remapped.length > 0) {
					newExplicitGroupSelection = remapped;
				}
			}
		}

		setAllGroups(groups);
		setSelectableGroups(newSelectableGroups);
		setDefaultGroupSelection(newDefaultGroupSelection);

		const newGroupSelection =
			newExplicitGroupSelection && newExplicitGroupSelection.length > 0
				? newExplicitGroupSelection
				: newDefaultGroupSelection;
		setGroupSelection(newGroupSelection);
		setExplicitGroupSelection(newExplicitGroupSelection);
		setGroupsInSelection(findGroupsInSelection(groups, newGroupSelection, canSelect));
		setLoading(false);
	}, [groups, canSelect, setPermissionBasedDefault, localStorageKey]);

	const updateExplicitGroupSelection = (newExplicitGroupSelection: GroupSelection[]) => {
		if (!allGroups) {
			return;
		}

		const newGroupSelection =
			newExplicitGroupSelection && newExplicitGroupSelection.length > 0
				? newExplicitGroupSelection
				: defaultGroupSelection;
		setGroupSelection(newGroupSelection);
		setGroupsInSelection(findGroupsInSelection(allGroups, newGroupSelection, canSelect));
		setExplicitGroupSelection(newExplicitGroupSelection);

		if (localStorageKey) {
			const localStorageGroupIDs = (newExplicitGroupSelection || []).map(
				({ group, recursive }) => group.id + (recursive ? '+' : ''),
			);
			window.localStorage.setItem(localStorageKey, localStorageGroupIDs.join(';'));
		}
	};

	return {
		allGroups,
		selectableGroups,
		groupsInSelection,
		groupSelection,
		hasExplicitGroupSelection:
			explicitGroupSelection !== undefined && explicitGroupSelection.length > 0,
		updateExplicitGroupSelection,
		loading,
	};
}

function useBatchedGroups(props: { types?: graphql.GroupType[] }) {
	const types = props.types;
	const viewer = useViewer(); // used to refresh groups when viewer changes
	const [groups, setGroups] = useState<graphql.Group[]>([]);
	const [finished, setFinished] = useState(false);

	const client = useApolloClient();

	useEffect(() => {
		setGroups([]);
		setFinished(false);

		(async () => {
			let endCursor: string | undefined;
			const newGroups: graphql.Group[] = [];

			do {
				const batch = await client.query<graphql.GroupsQuery>({
					query: graphql.GroupsDocument,
					variables: {
						first: 100,
						after: endCursor,
						filter: {
							isArchived: false,
							types,
						},
					},
					fetchPolicy: 'cache-first',
				});

				newGroups.push(...batch.data.groups.edges.map(({ node }) => node as graphql.Group));
				endCursor = batch.data.groups.pageInfo.endCursor ?? undefined;
			} while (endCursor);

			setGroups(newGroups);
			setFinished(true);
		})();
	}, [viewer, client, types]);

	return finished ? groups : undefined;
}

function groupsFromStorage(key: string, selectableGroups: graphql.Group[]) {
	const serialized = window.localStorage.getItem(key);
	const storedGroupIDs = serialized?.split(';');

	if (!storedGroupIDs || storedGroupIDs.length === 0) {
		return [];
	}

	return storedGroupIDs.flatMap((storedGroupID) => {
		const recursive = storedGroupID.endsWith('+');
		const groupID = storedGroupID.split('+')[0];
		const group = selectableGroups.find((group) => group.id === groupID);

		if (!group) {
			return [];
		}

		return [{ group, recursive }];
	});
}

const findGroupsInSelection = (
	allGroups: graphql.Group[],
	selection: GroupSelection[],
	canSelect: SelectableGroupFilter,
) => {
	const tree = trim(allGroups, canSelect);
	return selection.flatMap(({ group, recursive }) => {
		if (!recursive) {
			if (!canSelect(group)) {
				return [];
			}

			return [group];
		}

		const stack: graphql.Group[] = [group];
		const results: graphql.Group[] = [];
		const visited = new Set();

		while (stack.length > 0) {
			const parent = stack.pop()!;

			if (visited.has(parent.id)) {
				continue;
			}

			visited.add(parent.id);

			if (canSelect(parent)) {
				results.push(parent);
			}

			stack.push(...tree.filter((group) => group.parent?.id === parent.id));
		}

		return results;
	});
};
